import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { offlineDB } from '../utils/offlineDB';
import { toast } from 'sonner';

const OfflineContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  const checkPendingCount = useCallback(async () => {
    try {
      const pending = await offlineDB.getPendingSyncs();
      setPendingCount(pending.length);
    } catch (error) {
      console.error('Failed to check pending syncs:', error);
    }
  }, []);

  // Process individual sync item — pure function of args, no closure deps
  const processSyncItem = useCallback(async (sync) => {
    const { type, data } = sync;
    const authToken = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${authToken}` };

    switch (type) {
      case 'CREATE_FORM':
        await axios.post(`${API}/forms`, data, { headers });
        break;
      case 'UPDATE_FORM':
        await axios.put(`${API}/forms/${data.form_id}`, data, { headers });
        break;
      case 'SUBMIT_FORM':
        await axios.post(`${API}/forms/${data.form_id}/submit`, {}, { headers });
        break;
      case 'ADD_PAGE':
        await axios.post(`${API}/forms/${data.form_id}/add-page`, {}, { headers });
        break;
      default:
        console.warn('Unknown sync type:', type);
    }
  }, []);

  // Sync all pending changes to server
  const syncData = useCallback(async () => {
    if (!isOnline || isSyncing) return;

    setIsSyncing(true);
    try {
      const pendingSyncs = await offlineDB.getPendingSyncs();

      if (pendingSyncs.length === 0) {
        setLastSyncTime(new Date());
        setIsSyncing(false);
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const sync of pendingSyncs) {
        try {
          await processSyncItem(sync);
          await offlineDB.clearPendingSync(sync.id);
          successCount++;
        } catch (error) {
          console.error('Sync failed for item:', sync, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Synced ${successCount} changes`);
      }
      if (failCount > 0) {
        toast.error(`Failed to sync ${failCount} changes`);
      }

      setLastSyncTime(new Date());
      await checkPendingCount();
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Sync failed. Will retry later.');
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, processSyncItem, checkPendingCount]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online! Syncing data...');
      syncData();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline. Changes will be saved locally.');
    };

    const handleSwMessage = (event) => {
      if (event.data?.type === 'SYNC_REQUIRED') {
        syncData();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', handleSwMessage);
    }

    checkPendingCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', handleSwMessage);
      }
    };
  }, [syncData, checkPendingCount]);

  // Queue a change for sync
  const queueSync = useCallback(async (type, data) => {
    await offlineDB.addPendingSync(type, data);
    await checkPendingCount();
    if (isOnline) {
      syncData();
    }
  }, [isOnline, syncData, checkPendingCount]);

  const cacheForms = useCallback(async (forms) => {
    try {
      await offlineDB.saveForms(forms);
    } catch (error) {
      console.error('Failed to cache forms:', error);
    }
  }, []);

  const getCachedForms = useCallback(async () => {
    try {
      return await offlineDB.getAll('forms');
    } catch (error) {
      console.error('Failed to get cached forms:', error);
      return [];
    }
  }, []);

  const cacheTrades = useCallback(async (trades) => {
    try {
      await offlineDB.saveTrades(trades);
    } catch (error) {
      console.error('Failed to cache trades:', error);
    }
  }, []);

  const getCachedTrades = useCallback(async () => {
    try {
      return await offlineDB.getTrades();
    } catch (error) {
      console.error('Failed to get cached trades:', error);
      return [];
    }
  }, []);

  const registerBackgroundSync = useCallback(async () => {
    if ('serviceWorker' in navigator && 'sync' in window.registration) {
      try {
        await window.registration.sync.register('sync-forms');
      } catch (error) {
        console.log('Background sync not supported');
      }
    }
  }, []);

  const value = useMemo(
    () => ({
      isOnline,
      isSyncing,
      pendingCount,
      lastSyncTime,
      syncData,
      queueSync,
      cacheForms,
      getCachedForms,
      cacheTrades,
      getCachedTrades,
      registerBackgroundSync,
    }),
    [
      isOnline,
      isSyncing,
      pendingCount,
      lastSyncTime,
      syncData,
      queueSync,
      cacheForms,
      getCachedForms,
      cacheTrades,
      getCachedTrades,
      registerBackgroundSync,
    ],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
};

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};
